import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, Subscription, catchError, of, switchMap, timer } from 'rxjs';
import { environment } from '@env/environment';
import { ToastService } from './toaster';
import { Auth } from '../auth/auth';

export interface UploadResultBanner {
  variant: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  reportUrl?: string;
}

/**
 * Fallback check schedule for when the bulk-upload socket push doesn't arrive (confirmed
 * unreliable — see Header.initSocketNotifications). The first entry (0) captures which
 * notification ids already exist the moment the file is queued; each later entry re-checks for
 * a row that wasn't in that baseline. Spaced out (not a tight interval) so an outage still
 * surfaces a result without hammering the endpoint — 6 calls total over ~2 minutes, then gives up.
 */
const FALLBACK_CHECK_DELAYS_MS = [0, 4_000, 8_000, 16_000, 30_000, 60_000];

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseURL = environment.bulkUploadUrl;
  private readonly toaster = inject(ToastService);
  private readonly auth = inject(Auth);

  private pollSub?: Subscription;

  /**
   * Logout is an SPA navigation, not a page reload (see userprofile.ts / app.ts autoLogout), so
   * this root-provided singleton — and any in-flight fallback poll it started — otherwise survives
   * past logout and keeps hitting `notify` with the stale userId. `loggedIn.next(false)` is the one
   * signal both logout paths already emit, so this is the single place that needs to react to it.
   */
  constructor() {
    this.auth.loggedIn$.subscribe((isLoggedIn) => {
      if (!isLoggedIn) {
        this.stopWatching();
      }
    });
  }

  /**
   * Fires whenever something changes the notification list (a bulk upload finishing, a
   * mark-as-read, a clear-all) so the bell re-fetches. A plain Subject, not a BehaviorSubject:
   * subscribers only care about subsequent events, and a seeded initial value made the panel
   * issue a second, redundant GET on every page load.
   */
  notificationUpdated$ = new Subject<boolean>();

  /**
   * Drives the persistent, dismissible result banner (rendered once in MainLayout) that keeps a
   * failed/partial bulk-upload result visible until the user closes it — unlike the toast, which
   * auto-dismisses after a few seconds and can be missed. `next(null)` clears it.
   */
  uploadBanner$ = new Subject<UploadResultBanner | null>();

  /**
   * The `_` param is a cache-buster, not something the API reads. Without it every poll goes out
   * as a conditional request and comes back `304 Not Modified`, so a notification written between
   * two polls can be masked by the browser's cached copy. A unique URL per call has no cache entry
   * to validate against, forcing a real 200 every time.
   *
   * `userId` is required — without it the backend has no way to know whose notifications to
   * return and comes back with an empty list (matches `markasReadAll`/`clearAll` below, which
   * both take the same param for the same reason).
   */
  notificationGetByUserID(userId: string): Observable<any> {
    return this.http.get<any>(`${this.apiBaseURL}notify`, { params: { userId, _: Date.now() } });
  }

  markasRead(id: string): Observable<any> {
    return this.http.post(`${this.apiBaseURL}notify/read`, { id });
  }

  markasReadAll(userId: string): Observable<any> {
    return this.http.post(`${this.apiBaseURL}notify/read-all`, { userId });
  }

  sendMessagetoSocket(payload: any): Observable<any> {
    return this.http.post(`${this.apiBaseURL}notify/socket`, payload);
  }

  getAllNotifications(page = 0, size = 10): Observable<any> {
    return this.http.get<any>(`${this.apiBaseURL}notify/all`, {
      params: { page, size, _: Date.now() },
    });
  }

  clearAll(userId: string): Observable<any> {
    return this.http.post<any>(`${this.apiBaseURL}notify/clear-all`, { userId });
  }

  triggerNotificationUpdate(): void {
    this.notificationUpdated$.next(true);
  }

  showUploadBanner(banner: UploadResultBanner): void {
    this.uploadBanner$.next(banner);
  }

  dismissUploadBanner(): void {
    this.uploadBanner$.next(null);
  }

  /**
   * Safety net for when the bulk-upload socket push never lands. Checks a handful of times at
   * increasing delays instead of a tight interval, stopping the moment a "Bulk Upload"
   * notification created after `cutoff` shows up, or the socket beats it there
   * (Header.initSocketNotifications calls stopWatching() as soon as its own push arrives, so this
   * never doubles up the toast/banner for the same job).
   *
   * Matches by recency (createdAt >= cutoff) rather than diffing against a baseline snapshot of
   * existing ids: a validation failure can be written almost synchronously with the queuing
   * request (e.g. a malformed file fails column-mapping immediately), which would already be
   * present in the very first "baseline" read and so could never register as new under a diff.
   * The cutoff sits a few seconds before "now" to absorb the queuing request's own round-trip and
   * any clock skew between browser and server.
   */
  watchForBackgroundResult(): void {
    this.stopWatching();

    const user = this.auth.currentUser();
    const userId = user?.id || user?.userID;
    if (!userId) return;

    const cutoff = Date.now() - 15_000;
    let attempt = 0;

    const runNext = (): void => {
      if (attempt >= FALLBACK_CHECK_DELAYS_MS.length) return;
      const delay = FALLBACK_CHECK_DELAYS_MS[attempt];
      attempt++;

      this.pollSub = timer(delay)
        .pipe(switchMap(() => this.notificationGetByUserID(String(userId)).pipe(catchError(() => of(null)))))
        .subscribe((resp) => {
          if (!resp) {
            runNext();
            return;
          }

          const rows: any[] = resp?.data?.data?.rows ?? [];
          const resultRow = rows.find((r) => {
            const moduleType = (r?.moduleType || r?.title || '').toLowerCase();
            if (moduleType !== 'bulk upload') return false;
            const createdAt = new Date(r?.createdAt).getTime();
            return !isNaN(createdAt) && createdAt >= cutoff;
          });

          if (resultRow) {
            this.triggerNotificationUpdate();
            this.notifyUploadResult(resultRow);
          } else {
            runNext();
          }
        });
    };

    runNext();
  }

  /**
   * Translates a newly-arrived notification row into the same Success / "Upload completed with
   * errors" / Failed toast + banner shape Header.initSocketNotifications raises from the live
   * socket payload. The only confirmed signal here is the row's free-text `message`, so it's
   * parsed with the same "<n> processed, <n> failed" pattern the notifications bell parses.
   */
  private notifyUploadResult(row: any): void {
    const moduleType = (row?.moduleType || row?.title || '').toLowerCase();
    if (moduleType && moduleType !== 'bulk upload') {
      return;
    }

    const message: string = row?.message || '';
    const reportUrl: string = row?.actionUrl || row?.metadata?.url || row?.metadata?.failedFileUrl || '';
    const reportAction: [string?, (() => void)?] = reportUrl
      ? ['Download Error File', () => this.downloadReport(reportUrl)]
      : [];

    // A validation-only pass persists nothing, so its wording must not be read as an import result.
    const validationMatch = /(\d{1,9})\srow\(s\)\swould\simport,\s(\d{1,9})\srow\(s\)\swould\sfail/i.exec(message);
    if (validationMatch) {
      if (Number(validationMatch[2]) > 0) {
        this.toaster.warning('Validation completed with errors', message, 'top-right', ...reportAction);
      } else {
        this.toaster.success('Validation passed', message);
      }
      return;
    }

    const match = /(\d{1,9})\sprocessed,\s(\d{1,9})\sfailed/i.exec(message);

    if (match) {
      const successCount = Number(match[1]);
      const failureCount = Number(match[2]);

      if (failureCount === 0) {
        const successMessage = `${successCount} record(s) has been created successfully`;
        this.toaster.success('Success', successMessage);
        this.showUploadBanner({ variant: 'success', title: 'Success', message: successMessage });
      } else if (successCount === 0) {
        const failMessage = 'Upload has been failed due to some technical issue.';
        this.toaster.error('Failed', failMessage, 'top-right', ...reportAction);
        this.showUploadBanner({ variant: 'error', title: 'Failed', message: failMessage, reportUrl });
      } else {
        const summary = `${successCount} record(s) has been created successfully, ${failureCount} failed due to some field mapping error`;
        this.toaster.warning('Upload completed with errors', summary, 'top-right', ...reportAction);
        this.showUploadBanner({ variant: 'warning', title: 'Upload Completed with Errors', message: summary, reportUrl });
      }
      return;
    }

    if (/fail|error/i.test(message)) {
      const failMessage = message || 'Upload has been failed due to some technical issue.';
      this.toaster.error('Failed', failMessage, 'top-right', ...reportAction);
      this.showUploadBanner({ variant: 'error', title: 'Failed', message: failMessage, reportUrl });
    } else if (/success/i.test(message)) {
      const successMessage = message || 'Data uploaded successfully';
      this.toaster.success('Success', successMessage);
      this.showUploadBanner({ variant: 'success', title: 'Success', message: successMessage });
    } else {
      const infoMessage = message || 'Your upload has finished processing.';
      this.toaster.info('Upload completed', infoMessage);
      this.showUploadBanner({ variant: 'info', title: 'Upload completed', message: infoMessage, reportUrl });
    }
  }

  /**
   * Downloads a bulk-upload rejects report. The backend returns either an absolute cloud URL or a
   * path relative to the file host, so relative values are resolved before the download is started.
   */
  downloadReport(fileUrl: string): void {
    if (!fileUrl) return;

    let fullUrl = fileUrl;
    if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
      const baseUrl = environment.fileUrl.replace(/\/+$/, '');
      fullUrl = `${baseUrl}/${fileUrl.replace(/^\/+/, '')}`;
    }

    const rawName = fullUrl.split('/').pop()?.split('?')[0] || 'rejects-report.xlsx';
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = decodeURIComponent(rawName).replace(/^\d+_/, '');
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  stopWatching(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }
}
